import React from 'react'
import { Box } from '@mui/material'
import { format } from 'date-fns'
import { DatePickerSeeker, DateRangePickerSeeker } from '@ui-seeker/core'

import { AppPillToggle } from '@components'

/**
 * Date range picker with quick presets (project baseline — every RMS
 * dashboard must ship with this).
 *
 * Presets: YTD, MTD, Last 30 / 60 / 90, Custom.
 * Custom shows two date inputs and lets the user pick any From/To.
 *
 * Controlled component:
 *   props.value  = { preset: 'ytd'|'mtd'|'30'|'60'|'90'|'custom', from, to }
 *   props.onChange({ preset, from, to })
 */
const DEFAULT_PRESETS = [
	{ id: 'ytd',    label: 'YTD' },
	{ id: 'mtd',    label: 'MTD' },
	{ id: '30',     label: 'Last 30' },
	{ id: '60',     label: 'Last 60' },
	{ id: '90',     label: 'Last 90' },
	{ id: 'custom', label: 'Custom' },
]

// Forward-looking presets — for forecast-style dashboards. Anchored at
// `today` so they always reflect "next N days from now". The caller
// passes them via the `presets` prop along with a matching resolver.
export const FORWARD_PRESETS = [
	{ id: 'next7',   label: 'Next 7' },
	{ id: 'next30',  label: 'Next 30' },
	{ id: 'next60',  label: 'Next 60' },
	{ id: 'next90',  label: 'Next 90' },
	{ id: 'custom',  label: 'Custom' },
]

export function rangeForPreset(preset) {
	const today = new Date()
	const iso   = (d) => d.toISOString().slice(0, 10)
	if (preset === 'ytd')  return { from: `${today.getFullYear()}-01-01`, to: iso(today) }
	if (preset === 'mtd')  return { from: `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-01`, to: iso(today) }
	if (preset === '30' || preset === '60' || preset === '90') {
		const back = new Date(today); back.setDate(back.getDate() - Number(preset))
		return { from: iso(back), to: iso(today) }
	}
	if (preset === 'next7' || preset === 'next30' || preset === 'next60' || preset === 'next90') {
		const days = Number(preset.replace('next', ''))
		const fut  = new Date(today); fut.setDate(fut.getDate() + days)
		return { from: iso(today), to: iso(fut) }
	}
	return null  // custom: keep whatever the caller already had
}

// 'YYYY-MM-DD' ⇄ Date for the app's pickers, read as a local day
export const isoToDate = (s) => {
	if (!s) return null
	const [y, m, d] = s.split('-').map(Number)
	return new Date(y, m - 1, d)
}
export const dateToIso = (d) => d ? format(d, 'yyyy-MM-dd') : ''

// The pickers of these dashboards also look forward (forecast, market), so no day is disabled
const anyDay = () => false

/**
 * The preset pills, for the top row of the filter band.
 * onChange(value, { intent }) where intent is:
 *   - 'instant'  → preset click; host should apply immediately
 *   - 'pending'  → "Custom" alone does not change the dates
 */
export function DatePresets({ value, onChange, presets }) {
	const { from, to } = value
	const activePresets = presets || DEFAULT_PRESETS

	const select = (id) => {
		if (id === 'custom') {
			onChange({ preset: 'custom', from, to }, { intent: 'pending' })
		} else {
			const r = rangeForPreset(id)
			onChange({ preset: id, ...r }, { intent: 'instant' })
		}
	}

	return <AppPillToggle size="small"
	                      value={value.preset}
	                      options={activePresets.map(p => ({ value: p.id, label: p.label }))}
	                      onChange={select}/>
}

// "01 Jan 2026 ~ 19 Oct 2026" in a four-column band (the icon slot is fixed in the global styles): the value
// at the 14px of the app's other range field (DS Repricing) and an ellipsis as last resort
const rangeValueSx = {
	'& .rs-picker-toggle-value': {
		display: 'block', fontSize: '14px !important',
		overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
	},
}

// One From–To field showing the resolved dates behind any preset; picking dates waits for Apply
export function DateRangeField({ value, onChange, label = 'Date Range', disableFuture = false }) {
	const { from, to } = value
	return <DateRangePickerSeeker
		label={label}
		containerProps={{ sx: rangeValueSx }}
		value={from && to ? [isoToDate(from), isoToDate(to)] : null}
		onChange={(range) => {
			const [a, b] = range || []
			onChange({ preset: 'custom', from: dateToIso(a), to: dateToIso(b) }, { intent: 'pending' })
		}}
		{...!disableFuture && { shouldDisableDate: anyDay }}
		placement="bottomStart"
	/>
}

// A single day in the app's picker; empty clears it
export function DateField({ value, onChange, label, disableFuture = false, cleanable = false, ...props }) {
	return <DatePickerSeeker
		label={label}
		value={isoToDate(value)}
		onChange={(d) => onChange(dateToIso(d))}
		cleanable={cleanable}
		{...!disableFuture && { shouldDisableDate: anyDay }}
		placement="bottomStart"
		{...props}
	/>
}

/**
 * Presets and the range together, for a host that does not split them
 * between the top row and the fields of the filter band.
 */
export default function DateRangeFilter({ value, onChange, presets }) {
	return (
		<Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
			<DatePresets value={value} onChange={onChange} presets={presets}/>
			<Box sx={{ minWidth: 240 }}>
				<DateRangeField value={value} onChange={onChange}/>
			</Box>
		</Box>
	)
}
